import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogActions,
  Box, Grid, TextField, Typography, AppBar, Toolbar, IconButton,
  Autocomplete
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getIntlTranslation } from "../../../utils/helper";
import SaveButton from "../../../components/buttons/SaveButton";
import IntlMessages from "../../../utils/IntlMessages";
import { useIntl } from "react-intl";
import { validateSiteForm } from "../utils/siteValidationUtils";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "../../../api/userManagementApi";

export default function AddSite({
  open,
  onClose,
  onSubmit,
  onDownloadTemplate,
  currentUser,
  roles
}) {

  const dispatch = useDispatch();

  const [values, setValues] = useState({
    name: "",
    street: "",
    postalCode: "",
    city: "",
    country: "",
    clientName: "",
    clientEmail: "",
    siteManagerUserId: "",
    clientUserId: "",
  });

  const [users, setUsers] = useState([])
  const usersState = useSelector((state) => state.users);

  const intl = useIntl();

  const [touched, setTouched] = useState({}); // which fields were visited

  // compute errors whenever values change
  const errors = useMemo(() => validateSiteForm(values), [values]);
  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (
      usersState?.userList?.type === "FETCHED" &&
      Array.isArray(usersState?.userList?.data)
    ) {
      setUsers(usersState.userList.data);
    }
  }, [usersState?.userList]);

  const siteManagers = users?.filter(
    (u) => u.roles?.includes("PROJECT LEADER") || u.roles?.includes("Project Leader")
  );

  const propertyManagers = users?.filter(
    (u) => u.roles?.includes("User (External)")
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const showError = (field) => !!(touched[field] && errors[field]);
  const help = (field) => (touched[field] && errors[field]) ? errors[field] : " ";

  const handleSubmit = () => {
    const now = validateSiteForm(values);
    if (Object.keys(now).length > 0) {
      // mark all fields as touched so errors appear
      setTouched({
        name: true,
        street: true,
        postalCode: true,
        city: true,
        country: true,
        clientName: true,
        clientEmail: true,
      });
      return;
    }
    onSubmit?.(values);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{ unmountOnExit: true }}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <AppBar
        className="appBar"
        position="relative"
        sx={{ bgcolor: "background.paper", color: "text.primary" }}
      >
        <Toolbar className="dialog-toolbar" sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <IntlMessages id="mi.add-site" />
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="close"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <DialogContent dividers sx={{ px: 3, pt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <IntlMessages id="mi.site-name" />
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder={intl.formatMessage({ id: "mi.enter-site-name" })}
              value={values.name}
              name="name"
              inputProps={{ style: { textTransform: "capitalize" } }}
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("name")}
              helperText={help("name")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <IntlMessages id="mi.street" />
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder={intl.formatMessage({ id: "mi.enter-street" })}
              inputProps={{ style: { textTransform: "capitalize" } }}
              value={values.street}
              name="street"
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("street")}
              helperText={help("street")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <IntlMessages id="mi.postal-code" />
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder={intl.formatMessage({ id: "mi.enter-postal-code" })}
              type="number"
              value={values.postalCode}
              name="postalCode"
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("postalCode")}
              helperText={help("postalCode")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <IntlMessages id="_City" />
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder={intl.formatMessage({ id: "mi.enter-city" })}
              inputProps={{ style: { textTransform: "capitalize" } }}
              value={values.city}
              name="city"
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("city")}
              helperText={help("city")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <IntlMessages id="_Country" />
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder={intl.formatMessage({ id: "mi.enter-country" })}
              inputProps={{ style: { textTransform: "capitalize" } }}
              value={values.country}
              name="country"
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("country")}
              helperText={help("country")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <IntlMessages id="mi.client-name" />
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder={intl.formatMessage({ id: "mi.enter-client-name" })}
              value={values.clientName}
              inputProps={{ style: { textTransform: "capitalize" } }}
              name="clientName"
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("clientName")}
              helperText={help("clientName")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <IntlMessages id="mi.client-email" />
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder={intl.formatMessage({ id: "mi.enter-client-email" })}
              value={values.clientEmail}
              name="clientEmail"
              onChange={handleChange}
              onBlur={handleBlur}
              type="email"
              error={showError("clientEmail")}
              helperText={help("clientEmail")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Site Manager
            </Typography>
            <Autocomplete
              size="small"
              options={siteManagers}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={siteManagers.find((u) => u.userIdent === values.siteManagerUserId) || null}
              onChange={(event, newValue) => {
                setValues((prev) => ({
                  ...prev,
                  siteManagerUserId: newValue?.userIdent || null, // ✅ store UUID string
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Site Manager" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Property Owner/Manager
            </Typography>

            <Autocomplete
              size="small"
              options={propertyManagers}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              value={
                propertyManagers.find(
                  (u) => u.userIdent === values.clientUserId
                ) || null
              }
              onChange={(event, newValue) => {
                setValues((prev) => ({
                  ...prev,
                  clientUserId: newValue?.userIdent || null,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Property Owner/Manager"
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <SaveButton
          id="add-site-button"
          buttonText="add"
          variant="button"
          size="medium"
          style={{
            margin: 5,
            backgroundColor: "#d9534f",
            color: "#ffffff",
            height: 40,
            padding: "0 16px",
          }}
          tooltip={{ title: getIntlTranslation("tooltip.add-site-details") }}
          handleClick={handleSubmit}
          disabled={!isValid}
        />
      </DialogActions>
    </Dialog>
  );
}